import * as FirebaseAuth from "firebase/auth";
import type { User } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";

import { auth, firestore } from "@/firebase/web";

import {
  clearCachedVerifier,
  getOrCreateRecaptchaVerifier,
  mapConfirmError,
  mapSendError,
} from "./phoneVerification.web";

export type AccountLinkingErrorCode =
  | "NO_SIGNED_IN_USER"
  | "CREDENTIAL_ALREADY_IN_USE"
  | "PROVIDER_ALREADY_LINKED"
  | "REQUIRES_RECENT_LOGIN"
  | "USER_CANCELLED"
  | "POPUP_BLOCKED"
  | "INVALID_CREDENTIAL"
  | "ONLY_LINKED_PROVIDER"
  | "NETWORK_ERROR"
  | "UNKNOWN";

export class AccountLinkingError extends Error {
  code: AccountLinkingErrorCode;
  cause?: unknown;
  constructor(
    code: AccountLinkingErrorCode,
    message: string,
    cause?: unknown,
  ) {
    super(message);
    this.name = "AccountLinkingError";
    this.code = code;
    this.cause = cause;
  }
}

export type LinkPhoneConfirmation = {
  confirm: (code: string) => Promise<void>;
};

export type LinkPhoneParts = {
  prefix: string;
  number: string;
};

const setEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
};

export const mapLinkError = (error: unknown): AccountLinkingError => {
  if (error instanceof AccountLinkingError) return error;
  const code = (error as { code?: string })?.code;
  const errStr = String(error);
  const isCoopHint =
    /Cross-Origin-Opener-Policy/i.test(errStr) || /COOP/i.test(errStr);

  switch (code) {
    case "auth/credential-already-in-use":
    case "auth/account-exists-with-different-credential":
    case "auth/email-already-in-use":
      return new AccountLinkingError(
        "CREDENTIAL_ALREADY_IN_USE",
        "That sign-in method is already used by another account.",
        error,
      );
    case "auth/provider-already-linked":
      return new AccountLinkingError(
        "PROVIDER_ALREADY_LINKED",
        "This sign-in method is already linked to your account.",
        error,
      );
    case "auth/requires-recent-login":
      return new AccountLinkingError(
        "REQUIRES_RECENT_LOGIN",
        "Recent sign-in required.",
        error,
      );
    case "auth/popup-blocked":
      return new AccountLinkingError(
        "POPUP_BLOCKED",
        "Popup blocked.",
        error,
      );
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      if (isCoopHint) {
        return new AccountLinkingError(
          "POPUP_BLOCKED",
          "Popup was closed by browser policy.",
          error,
        );
      }
      return new AccountLinkingError("USER_CANCELLED", "User cancelled.", error);
    case "auth/invalid-credential":
      return new AccountLinkingError(
        "INVALID_CREDENTIAL",
        "Invalid credential.",
        error,
      );
    case "auth/network-request-failed":
      return new AccountLinkingError(
        "NETWORK_ERROR",
        "Network error.",
        error,
      );
    default:
      return new AccountLinkingError(
        "UNKNOWN",
        "Failed to link account.",
        error,
      );
  }
};

export const getLinkedProviders = (user: User): string[] => {
  return user.providerData.map((p) => p.providerId);
};

export const syncProvidersToFirestore = async (
  user: User,
): Promise<{ updated: boolean; providers: string[] }> => {
  const providers = getLinkedProviders(user);
  const userDocRef = doc(firestore, "users", user.uid);
  const snap = await getDoc(userDocRef);
  if (!snap.exists()) {
    return { updated: false, providers };
  }
  const existing = (snap.data() as { providers?: string[] }).providers ?? [];
  if (setEqual(existing, providers)) {
    return { updated: false, providers };
  }
  await updateDoc(userDocRef, { providers });
  return { updated: true, providers };
};

// Self-heals users/{uid}.phoneNumber.verified for accounts where Firebase Auth
// clearly has the phone provider linked but Firestore doesn't reflect it.
// The mismatch happens when a phone-auth flow authenticated the user but the
// follow-up Firestore write was skipped or interrupted (e.g. the old
// login.web.tsx code path that ignored phonePrefix/phoneNumber, or a signup
// interrupted between ensureUserDocExists and the phoneNumber merge). Both the
// PhoneVerifyBanner and the Settings → WhatsApp toggle gate on
// `hasPhoneProvider && phoneNumber.verified === true`, so leaving the mismatch
// in place keeps the banner up and the toggle disabled with no in-app recovery
// affordance. Idempotent: no-op when the provider is absent or Firestore
// already reflects verified:true.
export const syncPhoneVerifiedToFirestore = async (
  user: User,
): Promise<{ updated: boolean }> => {
  const hasPhoneProvider = user.providerData.some(
    (p) => p?.providerId === "phone",
  );
  if (!hasPhoneProvider) return { updated: false };

  const userDocRef = doc(firestore, "users", user.uid);
  const snap = await getDoc(userDocRef);
  if (!snap.exists()) return { updated: false };

  const existing = snap.data() as {
    phoneNumber?: {
      number?: string | null;
      prefix?: string | null;
      verified?: boolean;
    };
  };
  if (existing.phoneNumber?.verified === true) {
    return { updated: false };
  }

  await updateDoc(userDocRef, {
    phoneNumber: {
      verified: true,
      number: existing.phoneNumber?.number ?? user.phoneNumber ?? null,
      prefix: existing.phoneNumber?.prefix ?? null,
    },
  });
  return { updated: true };
};

const requireCurrentUser = (): User => {
  const current = auth.currentUser;
  if (!current) {
    throw new AccountLinkingError(
      "NO_SIGNED_IN_USER",
      "Must be signed in to link a provider.",
    );
  }
  return current;
};

// Resolves with the signed-in user once auth state settles, or null after the
// timeout. Handles the COOP race where linkWithPopup throws even though the
// link succeeded but auth.currentUser hasn't propagated yet. Mirrors the
// waitForCurrentUser helper in login.web.tsx.
const waitForProviderUpdate = (
  previousProviderCount: number,
  timeoutMs: number,
): Promise<boolean> => {
  return new Promise((resolve) => {
    const check = () => {
      const current = auth.currentUser;
      if (current && current.providerData.length > previousProviderCount) {
        return true;
      }
      return false;
    };
    if (check()) {
      resolve(true);
      return;
    }
    let done = false;
    const unsub = FirebaseAuth.onAuthStateChanged(auth, () => {
      if (done) return;
      if (check()) {
        done = true;
        clearTimeout(timer);
        unsub();
        resolve(true);
      }
    });
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      unsub();
      resolve(check());
    }, timeoutMs);
  });
};

export const linkGoogleProvider = async (user: User): Promise<void> => {
  const target = user ?? requireCurrentUser();
  const provider = new FirebaseAuth.GoogleAuthProvider();
  const previousProviderCount = target.providerData.length;

  try {
    try {
      await FirebaseAuth.linkWithPopup(target, provider);
    } catch (popupError: any) {
      const code = popupError?.code || "";
      const errStr = String(popupError);
      const isCoopHint =
        /Cross-Origin-Opener-Policy/i.test(errStr) || /COOP/i.test(errStr);

      if (code === "auth/cancelled-popup-request") {
        // Double-click on the button; the prior popup wins.
        return;
      }

      // COOP-affected browsers throw "popup-closed-by-user" even on a
      // successful link. Poll briefly before deciding.
      const settled = await waitForProviderUpdate(previousProviderCount, 1500);
      if (settled) {
        // link succeeded despite the throw
      } else if (
        code === "auth/popup-closed-by-user" &&
        !isCoopHint
      ) {
        throw new AccountLinkingError(
          "USER_CANCELLED",
          "User cancelled.",
          popupError,
        );
      } else if (code === "auth/popup-blocked" || isCoopHint) {
        // Redirect flow — control returns to the app after Google's
        // callback; getRedirectResult() in AuthContext picks it up.
        await FirebaseAuth.linkWithRedirect(target, provider);
        return;
      } else {
        throw popupError;
      }
    }
  } catch (error) {
    throw mapLinkError(error);
  }

  await target.reload();
  await syncProvidersToFirestore(target);
};

export const linkEmailProvider = async (
  user: User,
  email: string,
  password: string,
): Promise<void> => {
  const target = user ?? requireCurrentUser();
  try {
    const credential = FirebaseAuth.EmailAuthProvider.credential(
      email,
      password,
    );
    await FirebaseAuth.linkWithCredential(target, credential);
  } catch (error) {
    throw mapLinkError(error);
  }

  // Linking a password credential resets `emailVerified` to false even when the
  // user was previously verified via Google. Without a verification email the
  // user gets bounced to the verify-email screen on the next sign-in — kick off
  // the mail here. Swallow send failures: the link is already committed and the
  // user can re-request from the verify screen.
  try {
    await FirebaseAuth.sendEmailVerification(target);
  } catch {
    // ignore
  }

  await target.reload();
  await syncProvidersToFirestore(target);
};

export const linkPhoneProvider = async (
  user: User,
  phoneE164: string,
  phoneParts?: LinkPhoneParts,
): Promise<LinkPhoneConfirmation> => {
  const target = user ?? requireCurrentUser();

  const verifier = getOrCreateRecaptchaVerifier();
  let verificationId: string;
  try {
    const provider = new FirebaseAuth.PhoneAuthProvider(auth);
    verificationId = await provider.verifyPhoneNumber(phoneE164, verifier);
  } catch (error) {
    clearCachedVerifier();
    throw mapSendError(error);
  }

  return {
    confirm: async (code: string) => {
      try {
        const credential = FirebaseAuth.PhoneAuthProvider.credential(
          verificationId,
          code,
        );
        if (target.phoneNumber) {
          await FirebaseAuth.updatePhoneNumber(target, credential);
        } else {
          await FirebaseAuth.linkWithCredential(target, credential);
        }
      } catch (error) {
        throw mapConfirmError(error);
      }

      // Mirror the phone into the Firestore user doc so `groupInvite` (which
      // queries by `phoneNumber.prefix` + `phoneNumber.number`) keeps working.
      // WhatsApp notifications are NOT auto-enabled here — the user opts in
      // explicitly from Settings → Notifications, gated by the consent modal.
      if (phoneParts) {
        const userDocRef = doc(firestore, "users", target.uid);
        const snap = await getDoc(userDocRef);
        if (snap.exists()) {
          await updateDoc(userDocRef, {
            phoneNumber: {
              prefix: phoneParts.prefix,
              number: phoneParts.number,
              verified: true,
            },
          });
        }
      }

      await target.reload();
      await syncProvidersToFirestore(target);
    },
  };
};

export const unlinkProvider = async (
  user: User,
  providerId: string,
): Promise<void> => {
  const target = user ?? requireCurrentUser();

  const currentProviders = getLinkedProviders(target);
  if (!currentProviders.includes(providerId)) {
    // Nothing to do — treat as no-op rather than error.
    return;
  }
  if (currentProviders.length <= 1) {
    throw new AccountLinkingError(
      "ONLY_LINKED_PROVIDER",
      "Cannot unlink the only sign-in method.",
    );
  }

  try {
    await FirebaseAuth.unlink(target, providerId);
  } catch (error) {
    throw mapLinkError(error);
  }

  await target.reload();

  // Phone-specific Firestore side-effect: clear the verified flag since the
  // phone provider is no longer attached to the auth user. Keep the number
  // string as-is for display purposes.
  if (providerId === "phone") {
    const userDocRef = doc(firestore, "users", target.uid);
    const snap = await getDoc(userDocRef);
    if (snap.exists()) {
      const existing = snap.data() as {
        phoneNumber?: {
          number?: string | null;
          prefix?: string | null;
          verified?: boolean;
        };
      };
      if (existing.phoneNumber) {
        // Auto-disable WhatsApp notifications: the WA send channel requires a
        // verified phone, so symmetrize with the opt-in that happens on
        // linkPhoneProvider. Leave the monthly quota / usage / plan-start
        // untouched so re-verifying restores the same cycle.
        await updateDoc(userDocRef, {
          phoneNumber: {
            ...existing.phoneNumber,
            verified: false,
          },
          waPostNotificationsEnabled: false,
        });
      }
    }
  }

  await syncProvidersToFirestore(target);
};
